/**
 * Exact-time vs mechanic-shift coverage (#2796).
 *
 * Exact work stays on the clock the user chose. If that interval is not fully
 * inside a shift, callers warn — they never move the booking to the next shift.
 */

import { useMemo } from 'react';
import { addDays, format, parse } from 'date-fns';
import { useShiftSchedule, type ComputedShift } from '@/hooks/useShifts';
import type { SchedulingType } from '@/types';

const MS_PER_HOUR = 3_600_000;
const EMPTY_MECHANIC_IDS: string[] = [];

export interface ShiftCoverageWindow {
  startMs: number;
  endMs: number;
}

/**
 * Convert shift schedule rows to absolute local-wallclock windows.
 * Same `new Date(`${date}T${time}`)` interpretation as the calendar segmenter.
 */
export function parseShiftCoverageWindows(shifts: ComputedShift[]): ShiftCoverageWindow[] {
  const windows: ShiftCoverageWindow[] = [];
  for (const sh of shifts) {
    const startMs = new Date(`${sh.date}T${sh.startTime}`).getTime();
    const endMs = new Date(`${sh.date}T${sh.endTime}`).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    windows.push({ startMs, endMs });
  }
  return windows;
}

function isRangeInsideShift(
  startMs: number,
  endMs: number,
  window: ShiftCoverageWindow,
): boolean {
  if (endMs <= startMs) {
    return startMs >= window.startMs && startMs < window.endMs;
  }
  return startMs >= window.startMs && endMs <= window.endMs;
}

/**
 * True when `[startMs, endMs]` is not fully inside any shift window.
 * An empty window list means there is no covering shift (outside).
 * Zero-length intervals must fall strictly before the shift end, so 14:00
 * against a 06:00–14:00 shift is outside.
 */
export function isTimeRangeOutsideShifts(
  startMs: number,
  endMs: number,
  windows: ShiftCoverageWindow[],
): boolean {
  if (windows.length === 0) return true;
  return !windows.some((window) => isRangeInsideShift(startMs, endMs, window));
}

function uniqueShiftUserIds(shifts: ComputedShift[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const sh of shifts) {
    if (seen.has(sh.userId)) continue;
    seen.add(sh.userId);
    ids.push(sh.userId);
  }
  return ids;
}

/**
 * Exact-time picker / confirm check against selected mechanic shifts.
 * `scheduledAtLocal` is wizard state: `YYYY-MM-DDTHH:mm`.
 *
 * Coverage is per mechanic: warn when any selected mechanic lacks a single
 * window that fully contains the interval.
 */
export function isExactScheduleOutsideShift(input: {
  scheduledAtLocal: string;
  durationHours: number;
  shifts: ComputedShift[];
  mechanicIds?: string[];
}): boolean {
  const startMs = new Date(input.scheduledAtLocal).getTime();
  if (!Number.isFinite(startMs)) return false;
  const durationMs = Math.max(0, input.durationHours) * MS_PER_HOUR;
  const endMs = startMs + durationMs;
  const mechanicIds =
    input.mechanicIds && input.mechanicIds.length > 0
      ? input.mechanicIds
      : uniqueShiftUserIds(input.shifts);

  if (mechanicIds.length === 0) {
    return isTimeRangeOutsideShifts(startMs, endMs, parseShiftCoverageWindows(input.shifts));
  }

  return mechanicIds.some((id) =>
    isTimeRangeOutsideShifts(
      startMs,
      endMs,
      parseShiftCoverageWindows(input.shifts.filter((shift) => shift.userId === id)),
    ),
  );
}

export function segmentIsOutsideShift(item: { isOutsideShift?: boolean }): boolean {
  return item.isOutsideShift === true;
}

/**
 * Live "this exact time is outside the selected mechanic's shift" flag.
 * False while the schedule query has not succeeded, when no mechanic is
 * chosen, or when the type is floating — so the UI never warns on a guess.
 */
export function useExactTimeOutsideShiftWarning(opts: {
  schedulingType: SchedulingType;
  scheduledAtStr: string;
  durationHours: number;
  mechanicIds?: string[];
}): boolean {
  const mechanicIds = opts.mechanicIds ?? EMPTY_MECHANIC_IDS;
  const selectedDate = opts.scheduledAtStr ? (opts.scheduledAtStr.split('T')[0] ?? '') : '';
  const needsSchedule =
    opts.schedulingType === 'exact' && selectedDate !== '' && mechanicIds.length > 0;
  const rangeEnd = needsSchedule
    ? format(addDays(parse(selectedDate, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd')
    : '';
  const singleMechanicId = mechanicIds.length === 1 ? mechanicIds[0] : undefined;

  const { data: shifts = [], isSuccess } = useShiftSchedule({
    startDate: needsSchedule ? selectedDate : '',
    endDate: needsSchedule ? rangeEnd : '',
    ...(singleMechanicId ? { userId: singleMechanicId } : {}),
  });

  return useMemo(() => {
    if (opts.schedulingType !== 'exact') return false;
    // `isSuccess` is the pending signal: placeholder/stale rows must not warn.
    if (!opts.scheduledAtStr || mechanicIds.length === 0 || !isSuccess) return false;
    return isExactScheduleOutsideShift({
      scheduledAtLocal: opts.scheduledAtStr,
      durationHours: opts.durationHours,
      shifts,
      mechanicIds,
    });
  }, [
    opts.schedulingType,
    opts.scheduledAtStr,
    opts.durationHours,
    isSuccess,
    mechanicIds,
    shifts,
  ]);
}
